import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearSubmitterToken, formatApiErrorDetail } from "../../lib/api";
import PublicHeader from "../../components/PublicHeader";
import SiteFooter from "../../components/SiteFooter";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export const STATUS_LABELS = {
  started: "Started",
  vendors_done: "Credits added",
  submitted: "Submitted",
  in_review: "In review",
  accepted: "Accepted",
  not_this_time: "Not this time",
  in_publishing: "In publishing",
  published: "Published",
};

const nextStep = (s) => {
  if (!s.editable) return `/create-event/${s.id}/done`;
  if (!s.has_vendors) return `/create-event/${s.id}/vendors`;
  if (!s.has_story) return `/create-event/${s.id}/story`;
  return `/create-event/${s.id}/submit`;
};

export default function MySubmissions() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  const [removeFor, setRemoveFor] = useState(null);
  const [removeReason, setRemoveReason] = useState("");
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/my/submissions");
        setSubs(data.submissions || []);
      } catch {
        clearSubmitterToken();
        navigate("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, []);

  const invite = async (eventId) => {
    if (!inviteEmail.trim()) return toast.error("Add their email first.");
    try {
      await api.post(`/events/${eventId}/invite`, { email: inviteEmail.trim() });
      toast.success("Invite sent — they'll get a sign-in link by email.");
      setInviteFor(null);
      setInviteEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const [inviteFor, setInviteFor] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");

  const removeSubmission = async () => {
    if (!removeFor) return;
    setRemoving(true);
    try {
      await api.delete("/my/submissions/" + removeFor.id, { data: { reason: removeReason.trim() } });
      setSubs((current) => current.filter((s) => s.id !== removeFor.id));
      setRemoveFor(null);
      setRemoveReason("");
      toast.success("Submission removed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove this submission.");
    } finally {
      setRemoving(false);
    }
  };

  if (subs === null) {
    return <div className="min-h-screen bg-[#f5f3ed] flex items-center justify-center text-[#5c5852]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="my-submissions-page">
      <PublicHeader pub={null} />
      <main className="w-full max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16 flex-1">
        <p className="btw-step-label mb-3">Your stories</p>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-10">My submissions</h1>

        {subs.length === 0 && (
          <div className="btw-section text-sm text-black" data-testid="no-submissions">
            Nothing here yet. <Link to="/create-event" className="underline">Start a submission</Link>.
          </div>
        )}

        <div className="space-y-4">
          {subs.map((s) => (
            <div key={s.id} className="bg-white border border-[#c19872] rounded-xl p-5" data-testid={`submission-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-bold">{s.event_name}</p>
                  <p className="text-xs text-[#5c5852] mt-1 uppercase tracking-wider">
                    {s.publication} · {s.story_label || "story"} · {s.role}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {s.editable && !s.has_story && (
                    <span className="bg-white border border-[#c19872] text-black px-3 py-1 text-xs rounded-full" data-testid={`story-todo-${s.id}`}>
                      Story to finish
                    </span>
                  )}
                  <span className="bg-[#e2d3c0] text-black px-3 py-1 text-xs rounded-full" data-testid={`status-${s.id}`}>
                    {STATUS_LABELS[s.status] || s.status}
                  </span>
                </div>
              </div>

              {s.status === "not_this_time" && (
                <p className="text-sm text-[#5c5852] mt-3 italic" data-testid={`gentle-${s.id}`}>
                  Thank you so much for sharing your story with us. This one isn't the right
                  fit for our pages this time — we'd love to hear from you again.
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 mt-4">
                {s.editable && (
                  <Link to={nextStep(s)} data-testid={`continue-${s.id}`} className="btw-btn text-xs px-4 py-2 inline-flex items-center gap-2">
                    <span>Continue</span>
                    <span className="btw-btn-arrow"><ArrowRight size={12} /></span>
                  </Link>
                )}
                {!s.editable && s.status !== "not_this_time" && (
                  <Link to={`/create-event/${s.id}/done`} data-testid={`view-${s.id}`} className="btw-link text-sm">
                    View
                  </Link>
                )}
                {s.editable && (
                  inviteFor === s.id ? (
                    <span className="inline-flex items-center gap-2">
                      <input
                        data-testid={`invite-email-${s.id}`}
                        className="btw-input border border-[#d8cec0] px-3 py-1.5 bg-white text-sm"
                        placeholder="Their email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                      <button onClick={() => invite(s.id)} data-testid={`invite-send-${s.id}`} className="text-xs underline">Send</button>
                      <button onClick={() => setInviteFor(null)} className="text-xs text-[#5c5852]">Cancel</button>
                    </span>
                  ) : (
                    <button
                      onClick={() => { setInviteFor(s.id); setInviteEmail(""); }}
                      data-testid={`invite-${s.id}`}
                      className="text-sm text-black hover:underline"
                    >
                      Invite a co-editor
                    </button>
                  )
                )}
                {s.role !== "co-editor" && s.status !== "published" && (
                  <button
                    type="button"
                    onClick={() => { setRemoveFor(s); setRemoveReason(""); }}
                    data-testid={"remove-submission-" + s.id}
                    className="text-sm text-[#b35c5c] hover:underline"
                  >
                    Remove submission
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {removeFor && (
          <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center px-4" data-testid="remove-submission-dialog">
            <section role="dialog" aria-modal="true" aria-labelledby="remove-submission-title" className="w-full max-w-lg bg-white border border-[#c19872] p-6">
              <h2 id="remove-submission-title" className="text-xl font-bold mb-2">Remove this submission?</h2>
              <p className="text-sm text-[#5c5852] mb-4">
                This permanently removes the submission and its stored gallery folder. This cannot be undone.
              </p>
              <label htmlFor="remove-submission-reason" className="text-sm font-bold block mb-1">Reason (optional)</label>
              <textarea
                id="remove-submission-reason"
                data-testid="remove-submission-reason"
                className="w-full border border-[#d8cec0] p-3 text-sm mb-5"
                rows="3"
                maxLength="500"
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder="Tell us why you are removing it, if you’d like."
              />
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setRemoveFor(null)} disabled={removing} className="btw-btn-outline text-xs">Keep submission</button>
                <button type="button" onClick={removeSubmission} disabled={removing} data-testid="confirm-remove-submission" className="text-xs px-4 py-2 bg-[#b35c5c] text-white disabled:opacity-50">
                  {removing ? "Removing…" : "Remove permanently"}
                </button>
              </div>
            </section>
          </div>
        )}

        <div className="mt-10">
          <Link to="/create-event" className="btw-link" data-testid="new-submission-link">
            Start another submission
          </Link>
        </div>
      </main>
      <SiteFooter pub={null} />
    </div>
  );
}
